import { useState, useEffect } from 'react'

export interface PricingData {
  countryCode: string
  currentPrice: string
  comparePrice: string
  currencyCode: string
  loading: boolean
}

interface CountryPricing {
  current: number
  compare: number
  currency: string
  locale: string
  decimals: boolean
}

const PRICING_MAP: Record<string, CountryPricing> = {
  CL: {
    current: 13499,
    compare: 41499,
    currency: 'CLP',
    locale: 'es-CL',
    decimals: false,
  },
  AR: {
    current: 20999,
    compare: 65999,
    currency: 'ARS',
    locale: 'es-AR',
    decimals: false,
  },
  CO: {
    current: 56999,
    compare: 176999,
    currency: 'COP',
    locale: 'es-CO',
    decimals: false,
  },
  MX: {
    current: 269,
    compare: 829,
    currency: 'MXN',
    locale: 'es-MX',
    decimals: false,
  },
  UY: {
    current: 599,
    compare: 1849,
    currency: 'UYU',
    locale: 'es-UY',
    decimals: false,
  },
  ES: {
    current: 12.99,
    compare: 40.99,
    currency: 'EUR',
    locale: 'es-ES',
    decimals: true,
  },
  US: {
    current: 14.99,
    compare: 47,
    currency: 'USD',
    locale: 'en-US',
    decimals: true,
  },
}

const DEFAULT_PRICING = PRICING_MAP['US']

function formatPrice(amount: number, pricing: CountryPricing): string {
  try {
    return new Intl.NumberFormat(pricing.locale, {
      style: 'decimal',
      minimumFractionDigits: pricing.decimals ? 2 : 0,
      maximumFractionDigits: pricing.decimals ? 2 : 0,
    }).format(amount)
  } catch {
    return `${amount}`
  }
}

/** Fetch with a timeout (ms). Rejects if the request takes too long. */
function fetchWithTimeout(url: string, ms = 5000): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  return fetch(url, { signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  )
}

/**
 * Geo detection with a 3-source fallback chain:
 * 1. api.country.is  — fast, no rate limits, returns { country }
 * 2. ipwho.is        — generous free tier, returns { country_code }
 * 3. ipapi.co        — well-known fallback, returns { country_code }
 * Returns ISO 3166-1 alpha-2 country code or 'US' on failure.
 */
async function detectCountry(): Promise<string> {
  // Source 1 — api.country.is
  try {
    const res = await fetchWithTimeout('https://api.country.is/', 4000)
    const json = await res.json()
    if (json?.country && typeof json.country === 'string') {
      return json.country.toUpperCase()
    }
  } catch {
    // fall through
  }

  // Source 2 — ipwho.is
  try {
    const res = await fetchWithTimeout('https://ipwho.is/', 4000)
    const json = await res.json()
    if (json?.country_code && typeof json.country_code === 'string') {
      return json.country_code.toUpperCase()
    }
  } catch {
    // fall through
  }

  // Source 3 — ipapi.co
  try {
    const res = await fetchWithTimeout('https://ipapi.co/json/', 4000)
    const json = await res.json()
    if (json?.country_code && typeof json.country_code === 'string') {
      return json.country_code.toUpperCase()
    }
  } catch {
    // fall through
  }

  return 'US'
}

export function usePricing(): PricingData {
  const [data, setData] = useState<PricingData>({
    countryCode: 'US',
    currentPrice: formatPrice(DEFAULT_PRICING.current, DEFAULT_PRICING),
    comparePrice: formatPrice(DEFAULT_PRICING.compare, DEFAULT_PRICING),
    currencyCode: DEFAULT_PRICING.currency,
    loading: true,
  })

  useEffect(() => {
    let cancelled = false

    detectCountry()
      .then((code) => {
        if (cancelled) return
        const pricing = PRICING_MAP[code] ?? DEFAULT_PRICING
        setData({
          countryCode: code,
          currentPrice: formatPrice(pricing.current, pricing),
          comparePrice: formatPrice(pricing.compare, pricing),
          currencyCode: pricing.currency,
          loading: false,
        })
      })
      .catch(() => {
        if (cancelled) return
        setData({
          countryCode: 'US',
          currentPrice: formatPrice(DEFAULT_PRICING.current, DEFAULT_PRICING),
          comparePrice: formatPrice(DEFAULT_PRICING.compare, DEFAULT_PRICING),
          currencyCode: DEFAULT_PRICING.currency,
          loading: false,
        })
      })

    return () => {
      cancelled = true
    }
  }, [])

  return data
}
